import { useState, useMemo, useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { ChevronLeft, ChevronRight, Shuffle, EyeOff, Eye, ExternalLink } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { facts, species, shuffleArray } from '@/data';
import { useProgress } from '@/hooks/useProgress';
import { categoryLabels, type QuestionCategory } from '@/types';

type ViewMode = 'cards' | 'list';
type FilterCategory = QuestionCategory | 'all';

export function Learn() {
  const [viewMode, setViewMode] = useState<ViewMode>('cards');
  const [currentIndex, setCurrentIndex] = useState(0);
  const [filterCategory, setFilterCategory] = useState<FilterCategory>('all');
  const [shuffled, setShuffled] = useState(false);
  const [hideRead, setHideRead] = useState(false);
  const [direction, setDirection] = useState(0); // -1 for left, 1 for right
  const { markFactRead, hasReadFact, progress } = useProgress();

  // Track if we're currently swiping to prevent double-triggers
  const isSwipingRef = useRef(false);

  const displayFacts = useMemo(() => {
    let filtered = filterCategory === 'all'
      ? facts
      : facts.filter((f) => f.category === filterCategory);

    // Filter out read facts if hideRead is enabled
    if (hideRead) {
      filtered = filtered.filter((f) => !hasReadFact(f.id));
    }

    return shuffled ? shuffleArray(filtered) : filtered;
  }, [filterCategory, shuffled, hideRead, hasReadFact]);

  const currentFact = displayFacts[currentIndex];

  const handleNext = () => {
    if (isSwipingRef.current) return;
    setDirection(1);
    if (currentIndex < displayFacts.length - 1) {
      setCurrentIndex(currentIndex + 1);
    } else {
      setCurrentIndex(0);
    }
  };

  const handlePrev = () => {
    if (isSwipingRef.current) return;
    setDirection(-1);
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    } else {
      setCurrentIndex(displayFacts.length - 1);
    }
  };

  const handleShuffle = () => {
    setShuffled(!shuffled);
    setCurrentIndex(0);
  };

  const handleToggleHideRead = () => {
    setHideRead(!hideRead);
    setCurrentIndex(0);
  };

  // Mark fact as read when viewing
  const handleFactView = (factId: string) => {
    if (!hasReadFact(factId)) {
      markFactRead(factId);
    }
  };

  // Mark current fact as read
  if (currentFact && !hasReadFact(currentFact.id)) {
    markFactRead(currentFact.id);
  }

  // Swipe handling
  const swipeThreshold = 50;
  const swipeVelocityThreshold = 300;

  const handleDragEnd = (_event: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const { offset, velocity } = info;

    // Check if swipe was significant enough
    const swipedLeft = offset.x < -swipeThreshold || velocity.x < -swipeVelocityThreshold;
    const swipedRight = offset.x > swipeThreshold || velocity.x > swipeVelocityThreshold;

    if (swipedLeft) {
      isSwipingRef.current = true;
      handleNext();
      setTimeout(() => { isSwipingRef.current = false; }, 300);
    } else if (swipedRight) {
      isSwipingRef.current = true;
      handlePrev();
      setTimeout(() => { isSwipingRef.current = false; }, 300);
    }
  };

  // Animation variants for card transitions
  const cardVariants = {
    enter: (direction: number) => ({
      x: direction > 0 ? 300 : -300,
      opacity: 0,
    }),
    center: {
      x: 0,
      opacity: 1,
    },
    exit: (direction: number) => ({
      x: direction < 0 ? 300 : -300,
      opacity: 0,
    }),
  };

  const unreadCount = facts.filter(f => !hasReadFact(f.id)).length;

  return (
    <div className="max-w-lg mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-display font-bold text-slate-800 dark:text-white mb-2">
          Learn About Otters
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {progress.factsRead.length}/{facts.length} facts read
          {hideRead && unreadCount > 0 && (
            <span className="ml-2 text-otter-500">({unreadCount} unread showing)</span>
          )}
        </p>
      </div>

      {/* View Toggle & Controls */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setViewMode('cards')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'cards'
                ? 'bg-otter-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            Cards
          </button>
          <button
            onClick={() => setViewMode('list')}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
              viewMode === 'list'
                ? 'bg-otter-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            List
          </button>
        </div>

        <div className="flex items-center gap-2">
          {/* Hide Read Toggle */}
          <button
            onClick={handleToggleHideRead}
            className={`p-2 rounded-lg transition-colors ${
              hideRead
                ? 'bg-otter-100 text-otter-600 dark:bg-otter-900 dark:text-otter-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
            }`}
            title={hideRead ? 'Show all cards' : 'Hide read cards'}
          >
            {hideRead ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>

          {/* Shuffle Toggle */}
          <button
            onClick={handleShuffle}
            className={`p-2 rounded-lg transition-colors ${
              shuffled
                ? 'bg-otter-100 text-otter-600 dark:bg-otter-900 dark:text-otter-400'
                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
            }`}
            title={shuffled ? 'Show in order' : 'Shuffle cards'}
          >
            <Shuffle className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4 scrollbar-hide -mx-4 px-4">
        <button
          onClick={() => { setFilterCategory('all'); setCurrentIndex(0); }}
          className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
            filterCategory === 'all'
              ? 'bg-otter-500 text-white'
              : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
          }`}
        >
          All Facts
        </button>
        {Object.entries(categoryLabels).map(([key, label]) => (
          <button
            key={key}
            onClick={() => { setFilterCategory(key as QuestionCategory); setCurrentIndex(0); }}
            className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors ${
              filterCategory === key
                ? 'bg-otter-500 text-white'
                : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Card View */}
      {viewMode === 'cards' && currentFact && (
        <div className="space-y-4">
          {/* Swipeable Card Container */}
          <div className="relative overflow-hidden">
            <AnimatePresence mode="wait" custom={direction}>
              <motion.div
                key={currentFact.id}
                custom={direction}
                variants={cardVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={{ duration: 0.25, ease: 'easeInOut' }}
                drag="x"
                dragConstraints={{ left: 0, right: 0 }}
                dragElastic={0.2}
                onDragEnd={handleDragEnd}
                className="cursor-grab active:cursor-grabbing"
              >
                <Card className="min-h-[200px] flex flex-col select-none">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-4">
                      <Badge>{categoryLabels[currentFact.category]}</Badge>
                      {hasReadFact(currentFact.id) && (
                        <span className="text-green-500 text-sm">✓ Read</span>
                      )}
                    </div>

                    <p className="text-lg text-slate-800 dark:text-white leading-relaxed">
                      {currentFact.content}
                    </p>
                  </div>

                  {/* Related Species */}
                  {currentFact.relatedSpecies && currentFact.relatedSpecies.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700">
                      <p className="text-xs text-slate-500 mb-2">Related Species:</p>
                      <div className="flex flex-wrap gap-2">
                        {currentFact.relatedSpecies.map((speciesId) => {
                          const otter = species.find((s) => s.id === speciesId);
                          return otter ? (
                            <Link
                              key={speciesId}
                              to={`/species/${speciesId}`}
                              className="px-2 py-1 bg-otter-100 dark:bg-otter-900 text-otter-700 dark:text-otter-300 rounded text-sm hover:bg-otter-200 dark:hover:bg-otter-800 transition-colors"
                            >
                              {otter.commonName}
                            </Link>
                          ) : null;
                        })}
                      </div>
                    </div>
                  )}

                  {/* Source */}
                  <div className="mt-4 text-right">
                    <a
                      href={currentFact.source}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-slate-400 hover:text-otter-500 inline-flex items-center gap-1"
                    >
                      Source <ExternalLink className="w-3 h-3" />
                    </a>
                  </div>
                </Card>
              </motion.div>
            </AnimatePresence>

            {/* Swipe hint */}
            <p className="text-center text-xs text-slate-400 mt-2">
              Swipe left/right or use buttons to navigate
            </p>
          </div>

          {/* Navigation */}
          <div className="flex items-center justify-between">
            <Button variant="ghost" onClick={handlePrev}>
              <ChevronLeft className="w-5 h-5" />
              Previous
            </Button>

            <span className="text-sm text-slate-500">
              {currentIndex + 1} / {displayFacts.length}
            </span>

            <Button variant="ghost" onClick={handleNext}>
              Next
              <ChevronRight className="w-5 h-5" />
            </Button>
          </div>
        </div>
      )}

      {/* Empty state for cards when all are read and hideRead is on */}
      {viewMode === 'cards' && !currentFact && hideRead && (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">🎉</div>
          <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
            All caught up!
          </h3>
          <p className="text-slate-500 mb-4">
            You've read all {facts.length} facts in this category!
          </p>
          <Button variant="secondary" onClick={handleToggleHideRead}>
            <Eye className="w-4 h-4" />
            Show all facts
          </Button>
        </div>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <div className="space-y-3">
          {displayFacts.map((fact, index) => (
            <Card
              key={fact.id}
              className={`${hasReadFact(fact.id) ? 'border-green-200 dark:border-green-800' : ''}`}
              onClick={() => handleFactView(fact.id)}
            >
              <div className="flex items-start gap-3">
                <span className="text-sm text-slate-400 font-mono w-6">
                  {String(index + 1).padStart(2, '0')}
                </span>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-2">
                    <Badge className="text-xs">{categoryLabels[fact.category]}</Badge>
                    {hasReadFact(fact.id) && (
                      <span className="text-green-500 text-xs">✓</span>
                    )}
                  </div>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    {fact.content}
                  </p>
                  {fact.relatedSpecies && fact.relatedSpecies.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {fact.relatedSpecies.map((speciesId) => {
                        const otter = species.find((s) => s.id === speciesId);
                        return otter ? (
                          <Link
                            key={speciesId}
                            to={`/species/${speciesId}`}
                            className="text-xs text-otter-600 dark:text-otter-400 hover:underline"
                          >
                            {otter.commonName}
                          </Link>
                        ) : null;
                      })}
                    </div>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state for list */}
      {viewMode === 'list' && displayFacts.length === 0 && (
        <div className="text-center py-12">
          {hideRead ? (
            <>
              <div className="text-6xl mb-4">🎉</div>
              <h3 className="text-lg font-bold text-slate-800 dark:text-white mb-2">
                All caught up!
              </h3>
              <p className="text-slate-500 mb-4">
                You've read all the facts in this category!
              </p>
              <Button variant="secondary" onClick={handleToggleHideRead}>
                <Eye className="w-4 h-4" />
                Show all facts
              </Button>
            </>
          ) : (
            <p className="text-slate-500">No facts found for this category.</p>
          )}
        </div>
      )}
    </div>
  );
}
